// src/pages/Portfolio.tsx
import React, { useState } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { FaExternalLinkAlt, FaGithub, FaCode } from 'react-icons/fa';
import { useLang } from '../contexts/LanguageContext';

/* ── Helpers ───────────────────────────────────── */
const MONTHS_EN  = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const formatDate = (d: string) => {
  const [year, month] = d.split('-');
  if (!month) return year;
  return `${MONTHS_EN[parseInt(month, 10) - 1]} ${year}`;
};

const getCatColor = (t: string) => {
  if (t === 'Work')     return '#f59e0b';
  if (t === 'Personal') return '#22c55e';
  return '#8b5cf6';
};

/* ── Project data ──────────────────────────────── */
interface Project {
  title: string;
  description: string;
  stack: string;
  date: string;
  target: 'Personal' | 'Work' | 'School';
  liveUrl?: string;
  githubUrl?: string;
  featured?: boolean;
}

const PROJECTS: Project[] = [
  {title: 'Tickage.fr', description: 'Ticket management system for sellers', stack: 'React, Node.js, Neon', date: '2026-06', target: 'Personal', liveUrl: 'https://www.tickage.fr/', featured: true },
  { title: 'Dev Tools',          description: 'Developer productivity tools — GitHub stats, utilities & dashboards',   stack: 'Next.js',                                      date: '2026-05', target: 'Personal', liveUrl: 'https://dev-tools-five.vercel.app/',     featured: true },
  { title: 'Finoria',            description: 'Personal investment tracking & portfolio management platform',           stack: 'React, TypeScript',                            date: '2025-11', target: 'Personal', liveUrl: 'https://finoria-app.vercel.app/',                 featured: true },
  { title: 'Lisify',             description: 'Music discovery platform powered by streaming APIs',                    stack: 'React, TypeScript',                            date: '2025-10', target: 'Personal', liveUrl: 'https://pf-lisify.vercel.app/'},
  { title: 'NFT Marketplace',    description: 'Full-featured decentralized NFT marketplace — Annual School Project',   stack: 'React, Solidity, Hardhat, IPFS, Ethers.js, TypeScript, Docker', date: '2023-07', target: 'School' },
  {title: 'GitHub Stats Dashboard', description: 'Check GitHub statistics dashboard', stack: 'React', date: '2026-05', target: 'Personal', liveUrl: 'https://git-hub-stats-dashboard.vercel.app/', featured: true }, 

  { title: 'BTP SaaS',           description: 'Project management & billing platform for construction companies',      stack: 'TypeScript, Node.js, PostgreSQL',              date: '2026-04', target: 'Personal', liveUrl: 'https://btp-saas-blush.vercel.app/auth/sign-in', featured: true },
  { title: 'GitHub Stats Page',  description: 'Check GitHub statistics dashboard',                                     stack: 'React',                                        date: '2026-05', target: 'Personal', liveUrl: 'https://git-hub-stats-dashboard.vercel.app/' },
  { title: 'My Wedding Management', description: 'Wedding planning & budget management platform',                      stack: 'React, TypeScript',                            date: '2025-11', target: 'Personal' },
  { title: 'Lending & Borrowing dApp', description: 'Decentralized lending and borrowing application on blockchain',  stack: 'React, Solidity',                              date: '2025-04', target: 'School' },
  { title: 'Case Clicker Game',  description: 'CS2 case opening simulator game',                                       stack: 'React',                                        date: '2025-03', target: 'Personal' },
  { title: 'Dentist App',        description: 'Dental practice management application',                                 stack: 'React, TypeScript',                            date: '2024-11', target: 'Personal' },
  { title: 'Crowdfunding — Avalanche', description: 'Decentralized crowdfunding platform on Avalanche',               stack: 'React, TypeScript',                            date: '2024-11', target: 'School' },
  { title: 'Bricks Bot',         description: 'Real estate crowdfunding opportunity sniping bot',                      stack: 'Rust',                                         date: '2024-09', target: 'Personal' },
  { title: 'Rust Exercises',     description: 'Algorithm challenges and exercises in Rust',                             stack: 'Rust',                                         date: '2024-08', target: 'Personal' },
  { title: 'Tennis Club',        description: 'Club management and court booking platform',                             stack: 'React, TypeScript, Node.js, PostgreSQL',       date: '2024-07', target: 'Personal' },
  { title: 'PowerBI Dashboard',  description: 'Business intelligence dashboard for data analytics at Equans',          stack: 'PowerBI',                                      date: '2024-05', target: 'Work' },
  { title: 'Real-Time Messenger',description: 'Instant messaging app with WebSocket support',                           stack: 'MongoDB, Express, React, Node.js, Socket.io',  date: '2024-06', target: 'Personal' },
  { title: 'GYM SaaS',          description: 'SaaS platform for gym management',                                       stack: 'TypeScript, Node.js, MongoDB',                 date: '2024-01', target: 'Personal' },
  { title: 'PANK GUI',          description: 'Recruitment interface for World of Tanks guild management',               stack: 'React, API, MongoDB',                          date: '2024-03', target: 'Personal' },
  { title: 'Web3 Betting Platform', description: 'On-chain betting platform with wallet integration and design patterns', stack: 'Solidity, TypeScript',                    date: '2024-04', target: 'School' },
  { title: 'NFC × Blockchain',  description: 'Hackathon — NFC technology combined with blockchain',                    stack: 'JavaScript, NFC, Blockchain',                  date: '2024-04', target: 'School' },
  { title: 'API Gateway — FasTicket', description: 'Microservices architecture with API gateway',                      stack: 'TypeScript, Node.js, Express, MongoDB, JWT',   date: '2023-10', target: 'School' },
  { title: 'CTF — GoLang',      description: 'Backend retrieving all GitHub repositories of a given user',              stack: 'Go',                                           date: '2023-09', target: 'School' },
  { title: 'Zoo Plan API',       description: 'RESTful API for zoo management with authentication',                     stack: 'Node.js, Express, MongoDB, JWT, React',        date: '2023-07', target: 'School' },
  { title: 'Unity 3D Game',     description: '3D game development with Unity engine',                                   stack: 'Unity 3D, C#',                                 date: '2022-03', target: 'School' },
  { title: 'LoyaltyCard',       description: 'Loyalty card system — Annual Project',                                    stack: 'HTML/CSS/JS, PHP, SQL, VMware',                date: '2022-01', target: 'School' },
  { title: 'Annual Project 2024',description: 'Full-stack dApp with multi-technology stack — Go API + Solidity + React frontend', stack: 'React, TypeScript, Foundry, Solidity, Go', date: '2024-07', target: 'School'},
  { title: 'WebGL Project',     description: 'WebGL 3D graphics programming',                                           stack: 'JavaScript, WebGL',                            date: '2021-10', target: 'School' },
  { title: 'FindOrderEnjoy',    description: 'Online restaurant ordering platform — Annual Project',                    stack: 'HTML/CSS/JS/PHP',                              date: '2021-05', target: 'School' },
  { title: 'Bowling in C',      description: 'Bowling game scoring system in C',                                        stack: 'C',                                            date: '2020-11', target: 'School' },
];

/* ── Styled components ─────────────────────────── */
const PortfolioContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 7rem 2rem 5rem;
  background: ${(p) => p.theme.gradients.background};
  min-height: 100vh;
`;

const SectionLabel = styled(motion.div)`
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: ${(p) => p.theme.colors.accent};
  background: rgba(139,92,246,0.08);
  border: 1px solid rgba(139,92,246,0.22);
  padding: 0.35rem 1rem;
  border-radius: 50px;
  margin-bottom: 1.2rem;
`;

const Title = styled(motion.h1)`
  font-size: clamp(2rem, 4vw, 3rem);
  font-weight: 800;
  letter-spacing: -0.03em;
  margin-bottom: 0.8rem;
  text-align: center;

  span {
    background: linear-gradient(135deg, #7c3aed, #a78bfa);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }
`;

const Subtitle = styled(motion.p)`
  color: ${(p) => p.theme.colors.muted};
  font-size: 1rem;
  max-width: 520px;
  text-align: center;
  margin-bottom: 2.5rem;
`;

/* ── Filters ───────────────────────────────────── */
const FiltersBar = styled(motion.div)`
  display: flex;
  gap: 1.5rem;
  margin-bottom: 2rem;
  flex-wrap: wrap;
  justify-content: center;
  align-items: center;
`;

const FilterGroup = styled.div`
  display: flex;
  gap: 0.3rem;
  align-items: center;
`;

const FilterLabel = styled.span`
  font-size: 0.75rem;
  font-weight: 600;
  color: ${(p) => p.theme.colors.muted};
  margin-right: 0.2rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const FilterPill = styled.button<{ $active: boolean }>`
  font-size: 0.8rem;
  font-weight: 500;
  padding: 0.38rem 0.9rem;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.22s ease;
  font-family: inherit;
  ${({ $active, theme }) =>
    $active
      ? `background: rgba(139,92,246,0.15); color: ${theme.colors.accentLight}; border: 1px solid rgba(139,92,246,0.35);`
      : `background: transparent; color: ${theme.colors.muted}; border: 1px solid rgba(255,255,255,0.07);
         &:hover { border-color: rgba(139,92,246,0.25); color: ${theme.colors.white}; }`}
`;

const ResultCount = styled.p`
  font-size: 0.8rem;
  color: ${(p) => p.theme.colors.muted};
  margin-bottom: 1.8rem;
  span { color: ${(p) => p.theme.colors.accentLight}; font-weight: 600; }
`;

/* ── Featured section ──────────────────────────── */
const FeaturedHeading = styled.h2`
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.15em;
  text-transform: uppercase;
  color: ${(p) => p.theme.colors.muted};
  margin-bottom: 1.2rem;
  width: 100%;
  max-width: 1200px;
  text-align: left;
  display: flex;
  align-items: center;
  gap: 0.7rem;

  &::after {
    content: '';
    flex: 1;
    height: 1px;
    background: rgba(255,255,255,0.07);
  }
`;

const FeaturedGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1.2rem;
  width: 100%;
  max-width: 1200px;
  margin-bottom: 3rem;

  @media (max-width: 1000px) { grid-template-columns: repeat(2, 1fr); }
  @media (max-width: 620px)  { grid-template-columns: 1fr; }
`;

const FeaturedCard = styled(motion.a)`
  background: #0e0e12;
  border: 1px solid rgba(255,255,255,0.07);
  border-radius: 18px;
  padding: 1.8rem 2rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  text-decoration: none;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0; left: 0;
    width: 100%; height: 3px;
    background: linear-gradient(90deg, #7c3aed, #a78bfa, #22c55e);
    opacity: 0;
    transition: opacity 0.3s ease;
  }

  &:hover {
    border-color: rgba(139,92,246,0.3);
    transform: translateY(-4px);
    box-shadow: 0 0 0 1px rgba(139,92,246,0.12), 0 24px 48px rgba(0,0,0,0.4);
    &::before { opacity: 1; }
  }
`;

const FeaturedCardNonLink = styled(motion.div)`
  background: #0e0e12;
  border: 1px solid rgba(255,255,255,0.07);
  border-radius: 18px;
  padding: 1.8rem 2rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  cursor: default;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0; left: 0;
    width: 100%; height: 3px;
    background: linear-gradient(90deg, #7c3aed, #a78bfa, #22c55e);
    opacity: 0;
    transition: opacity 0.3s ease;
  }

  &:hover {
    border-color: rgba(139,92,246,0.2);
    transform: translateY(-3px);
    box-shadow: 0 0 0 1px rgba(139,92,246,0.08), 0 20px 40px rgba(0,0,0,0.35);
    &::before { opacity: 1; }
  }
`;

const FeaturedMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 0.7rem;
`;

const CatBadge = styled.span<{ $color: string }>`
  font-size: 0.65rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  padding: 0.2rem 0.65rem;
  border-radius: 20px;
  color: ${(p) => p.$color};
  background: ${(p) => p.$color}12;
  border: 1px solid ${(p) => p.$color}28;
`;

const FeaturedDate = styled.span`
  font-size: 0.78rem;
  color: ${(p) => p.theme.colors.muted};
  margin-left: auto;
`;

const FeaturedTitle = styled.h3`
  font-size: 1.3rem;
  font-weight: 700;
  color: ${(p) => p.theme.colors.white};
  line-height: 1.2;
`;

const FeaturedDesc = styled.p`
  font-size: 0.9rem;
  color: ${(p) => p.theme.colors.light};
  line-height: 1.65;
  margin: 0;
  flex: 1;
`;

const StackRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem;
`;

const StackPill = styled.span`
  font-size: 0.68rem;
  font-weight: 500;
  color: ${(p) => p.theme.colors.accentLight};
  background: rgba(139,92,246,0.08);
  border: 1px solid rgba(139,92,246,0.15);
  padding: 0.2rem 0.55rem;
  border-radius: 8px;
  font-family: ${(p) => p.theme.fonts.code};
`;

const FeaturedActions = styled.div`
  display: flex;
  gap: 0.6rem;
  padding-top: 0.5rem;
  border-top: 1px solid rgba(255,255,255,0.05);
  margin-top: auto;
`;

const ActionBtn = styled.a<{ $primary?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 0.8rem;
  font-weight: 600;
  padding: 0.5rem 1.1rem;
  border-radius: 8px;
  transition: all 0.25s ease;
  cursor: pointer;
  text-decoration: none;

  ${({ $primary, theme }) =>
    $primary
      ? `background: linear-gradient(135deg, #7c3aed, #9b6cfc); color: #fff; border: none;
         &:hover { box-shadow: 0 0 20px rgba(124,58,237,0.4); transform: translateY(-1px); }`
      : `background: transparent; color: ${theme.colors.muted}; border: 1px solid rgba(255,255,255,0.08);
         &:hover { border-color: rgba(139,92,246,0.3); color: ${theme.colors.accentLight}; background: rgba(139,92,246,0.06); }`}
`;

/* ── Compact list section ──────────────────────── */
const ListHeading = styled(FeaturedHeading)``;

const ProjectList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
  width: 100%;
  max-width: 1200px;
  border: 1px solid rgba(255,255,255,0.07);
  border-radius: 16px;
  overflow: hidden;
`;

const ProjectRow = styled(motion.div)`
  display: grid;
  grid-template-columns: 1fr auto;
  align-items: center;
  gap: 1rem;
  padding: 1rem 1.4rem;
  border-bottom: 1px solid rgba(255,255,255,0.05);
  transition: background 0.2s ease;
  background: #0e0e12;

  &:last-child { border-bottom: none; }

  &:hover {
    background: rgba(139,92,246,0.04);
  }

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
    gap: 0.5rem;
  }
`;

const RowLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  min-width: 0;
`;

const RowIcon = styled.div<{ $color: string }>`
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: ${(p) => p.$color}12;
  border: 1px solid ${(p) => p.$color}22;
  color: ${(p) => p.$color};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.8rem;
  flex-shrink: 0;
`;

const RowInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
  min-width: 0;
`;

const RowTitle = styled.span`
  font-size: 0.9rem;
  font-weight: 600;
  color: ${(p) => p.theme.colors.white};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const RowMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
`;

const RowDate = styled.span`
  font-size: 0.72rem;
  color: ${(p) => p.theme.colors.muted};
`;

const RowStack = styled.span`
  font-size: 0.68rem;
  color: ${(p) => p.theme.colors.muted};
  font-family: ${(p) => p.theme.fonts.code};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 280px;
`;

const RowRight = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-shrink: 0;
`;

const SmallLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  font-size: 0.72rem;
  font-weight: 600;
  padding: 0.3rem 0.7rem;
  border-radius: 6px;
  text-decoration: none;
  transition: all 0.2s ease;
  color: ${(p) => p.theme.colors.accentLight};
  background: rgba(139,92,246,0.08);
  border: 1px solid rgba(139,92,246,0.15);

  &:hover {
    background: rgba(139,92,246,0.15);
    border-color: rgba(139,92,246,0.35);
  }
`;

/* ── Component ─────────────────────────────────── */
const Portfolio: React.FC = () => {
  const { t } = useLang();
  const [selectedTarget, setSelectedTarget] = useState<string>('All');
  const [selectedYear,   setSelectedYear]   = useState<string>('All');

  const uniqueTargets = ['All', 'Personal', 'Work', 'School'];
  const uniqueYears   = ['All', ...Array.from(new Set(PROJECTS.map((p) => p.date.split('-')[0]))).sort((a, b) => +b - +a)];

  const filtered = PROJECTS.filter((p) => {
    const okTarget = selectedTarget === 'All' || p.target === selectedTarget;
    const okYear   = selectedYear   === 'All' || p.date.startsWith(selectedYear);
    return okTarget && okYear;
  });

  const featured  = filtered.filter((p) => p.featured);
  const compact   = filtered.filter((p) => !p.featured);

  const cardVariants = {
    hidden: { opacity: 0, y: 18 },
    visible: (i: number) => ({ opacity: 1, y: 0, transition: { duration: 0.4, delay: i * 0.07 } }),
  };

  const renderFeaturedCard = (p: Project, i: number) => {
    const color = getCatColor(p.target);
    const label = t.portfolio.categories[p.target as keyof typeof t.portfolio.categories] ?? p.target;
    const inner = (
      <>
        <FeaturedMeta>
          <CatBadge $color={color}>{label}</CatBadge>
          <FeaturedDate>{formatDate(p.date)}</FeaturedDate>
        </FeaturedMeta>
        <FeaturedTitle>{p.title}</FeaturedTitle>
        <FeaturedDesc>{p.description}</FeaturedDesc>
        <StackRow>
          {p.stack.split(/[,&]/).map((s) => s.trim()).filter(Boolean).map((s, si) => (
            <StackPill key={si}>{s}</StackPill>
          ))}
        </StackRow>
        {(p.liveUrl || p.githubUrl) && (
          <FeaturedActions>
            {p.liveUrl && (
              <ActionBtn href={p.liveUrl} target="_blank" rel="noopener noreferrer" $primary onClick={(e) => e.stopPropagation()}>
                <FaExternalLinkAlt size={10} /> {t.portfolio.viewLive}
              </ActionBtn>
            )}
            {p.githubUrl && (
              <ActionBtn href={p.githubUrl} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}>
                <FaGithub size={12} /> {t.portfolio.viewGithub}
              </ActionBtn>
            )}
          </FeaturedActions>
        )}
      </>
    );

    return p.liveUrl ? (
      <FeaturedCard
        key={p.title}
        href={p.liveUrl}
        target="_blank"
        rel="noopener noreferrer"
        custom={i}
        variants={cardVariants}
        initial="hidden"
        animate="visible"
      >
        {inner}
      </FeaturedCard>
    ) : (
      <FeaturedCardNonLink
        key={p.title}
        custom={i}
        variants={cardVariants}
        initial="hidden"
        animate="visible"
      >
        {inner}
      </FeaturedCardNonLink>
    );
  };

  return (
    <PortfolioContainer id="portfolio">
      <SectionLabel
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        {t.portfolio.sectionLabel}
      </SectionLabel>

      <Title
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.1 }}
      >
        {t.portfolio.title} <span>{t.portfolio.titleHighlight}</span>
      </Title>

      <Subtitle
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.2, duration: 0.6 }}
      >
        {t.portfolio.subtitle}
      </Subtitle>

      <FiltersBar
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.3 }}
      >
        <FilterGroup>
          <FilterLabel>{t.portfolio.filterCategory}:</FilterLabel>
          {uniqueTargets.map((tgt) => (
            <FilterPill
              key={tgt}
              $active={selectedTarget === tgt}
              onClick={() => setSelectedTarget(tgt)}
            >
              {tgt === 'All' ? t.portfolio.all : (t.portfolio.categories[tgt as keyof typeof t.portfolio.categories] ?? tgt)}
            </FilterPill>
          ))}
        </FilterGroup>

        <FilterGroup>
          <FilterLabel>{t.portfolio.filterYear}:</FilterLabel>
          {uniqueYears.map((yr) => (
            <FilterPill
              key={yr}
              $active={selectedYear === yr}
              onClick={() => setSelectedYear(yr)}
            >
              {yr === 'All' ? t.portfolio.all : yr}
            </FilterPill>
          ))}
        </FilterGroup>
      </FiltersBar>

      <ResultCount>
        <span>{filtered.length}</span> {t.portfolio.results(filtered.length).replace(`${filtered.length} `, '')}
      </ResultCount>

      <AnimatePresence>
        {featured.length > 0 && (
          <>
            <FeaturedHeading>{t.portfolio.featured}</FeaturedHeading>
            <FeaturedGrid>
              {featured.map((p, i) => renderFeaturedCard(p, i))}
            </FeaturedGrid>
          </>
        )}

        {compact.length > 0 && (
          <>
            <ListHeading>{t.portfolio.allProjects}</ListHeading>
            <ProjectList>
              {compact.map((p, i) => {
                const color = getCatColor(p.target);
                const label = t.portfolio.categories[p.target as keyof typeof t.portfolio.categories] ?? p.target;
                return (
                  <ProjectRow
                    key={p.title}
                    custom={i}
                    variants={cardVariants}
                    initial="hidden"
                    animate="visible"
                  >
                    <RowLeft>
                      <RowIcon $color={color}><FaCode /></RowIcon>
                      <RowInfo>
                        <RowTitle>{p.title}</RowTitle>
                        <RowMeta>
                          <CatBadge $color={color}>{label}</CatBadge>
                          <RowDate>{formatDate(p.date)}</RowDate>
                          <RowStack>{p.stack}</RowStack>
                        </RowMeta>
                      </RowInfo>
                    </RowLeft>
                    <RowRight>
                      {p.liveUrl && (
                        <SmallLink href={p.liveUrl} target="_blank" rel="noopener noreferrer">
                          <FaExternalLinkAlt size={9} /> Live
                        </SmallLink>
                      )}
                      {p.githubUrl && (
                        <SmallLink href={p.githubUrl} target="_blank" rel="noopener noreferrer">
                          <FaGithub size={11} /> GitHub
                        </SmallLink>
                      )}
                    </RowRight>
                  </ProjectRow>
                );
              })}
            </ProjectList>
          </>
        )}
      </AnimatePresence>
    </PortfolioContainer>
  );
};

export default Portfolio;
